import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Platform, ParseResult } from '../types';
import { describePlaylist, describeYouTubePlaylist, describeAppleMusicPlaylist, getAudioFeatures, getTrackDetails, PlaylistInfo, TrackInfo } from '../services/emotifyService';
import { sendTelemetryEvent } from '../services/telemetryService';

export type AnalysisStep = 'fetching' | 'analyzing' | 'building' | 'complete' | 'error';

interface AppContextType {
  currentPlaylistId: string | null;
  playlistInfo: PlaylistInfo | null;
  tracks: TrackInfo[];
  audioFeatures: any[];
  isAnalyzing: boolean;
  analysisProgress: string;
  analysisStep: AnalysisStep;
  error: string | null;
  platform: Platform | null;
  analyzePlaylist: (result: ParseResult) => Promise<boolean>;
  /** Re-run the most recent analysis attempt. No-op (returns false) if there's nothing to retry. */
  retryAnalysis: () => Promise<boolean>;
  clearPlaylist: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentPlaylistId, setCurrentPlaylistId] = useState<string | null>(null);
  const [playlistInfo, setPlaylistInfo] = useState<PlaylistInfo | null>(null);
  const [tracks, setTracks] = useState<TrackInfo[]>([]);
  const [audioFeatures, setAudioFeatures] = useState<any[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [analysisStep, setAnalysisStep] = useState<AnalysisStep>('fetching');
  const [error, setError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  // Remember the most recent attempt so the results screen can offer a retry
  // instead of a dead-end "go back" when analysis fails (e.g. a transient 502).
  const [lastAttempt, setLastAttempt] = useState<ParseResult | null>(null);

  useEffect(() => {
    const id = localStorage.getItem('lastPlaylistId');
    if (id) setCurrentPlaylistId(id);
  }, []);

  /** Returns true on success, false on error */
  const analyzePlaylist = async (result: ParseResult): Promise<boolean> => {
    setLastAttempt(result);
    setIsAnalyzing(true);
    setError(null);
    setAnalysisProgress('Validating...');
    setAnalysisStep('fetching');

    try {
      const { platform: detectedPlatform, playlistId } = result;
      setPlatform(detectedPlatform);

      setAnalysisProgress('Loading playlist...');
      const descResult = detectedPlatform === 'ytmusic'
        ? await describeYouTubePlaylist(playlistId)
        : detectedPlatform === 'apple'
          ? await describeAppleMusicPlaylist(playlistId, result.storefront)
          : await describePlaylist(playlistId);
      if (descResult.error) throw new Error(descResult.error.message);

      setAnalysisStep('analyzing');
      const { playlist, tracks: trackList } = descResult.data!;
      setPlaylistInfo(playlist);
      setTracks(trackList);

      if (trackList.length === 0) throw new Error('This playlist appears to be empty.');

      const trackIds = trackList.map(t => t.id);
      setAnalysisProgress(`Analyzing ${trackIds.length} tracks...`);

      // Fetch audio features and track details (album art) in parallel
      const [featResult, detailsResult] = await Promise.all([
        getAudioFeatures(trackIds),
        getTrackDetails(trackIds),
      ]);
      if (featResult.error) throw new Error(featResult.error.message);

      setAnalysisStep('building');
      // Build image map from Spotify track details
      const imageMap = new Map<string, string>();
      if (detailsResult.data?.tracks) {
        detailsResult.data.tracks.forEach(t => {
          if (t.image) imageMap.set(t.id, t.image);
        });
      }

      // Enrich audio features with track name/artist/image
      const trackMap = new Map(trackList.map(t => [t.id, t]));
      const enriched = (featResult.data!.audioFeatures || []).map((f: any) => {
        const sid = f.spotify_id || f.id;
        const info = trackMap.get(sid);
        return {
          ...f,
          spotify_id: sid,
          name: info?.name || f.name || 'Unknown',
          artist: info?.artist || f.artist || 'Unknown',
          albumImage: imageMap.get(sid) || '',
        };
      });

      setAudioFeatures(enriched);
      setCurrentPlaylistId(playlistId);
      localStorage.setItem('lastPlaylistId', playlistId);
      setAnalysisProgress('');
      setAnalysisStep('complete');
      sendTelemetryEvent({ eventType: 'playlist_analyzed', platform: detectedPlatform, trackCount: trackList.length, playlistId });
      setIsAnalyzing(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setAnalysisProgress('');
      setAnalysisStep('error');
      setIsAnalyzing(false);
      return false;
    }
  };

  const retryAnalysis = async (): Promise<boolean> => {
    if (!lastAttempt) return false;
    return analyzePlaylist(lastAttempt);
  };

  const clearPlaylist = () => {
    setCurrentPlaylistId(null);
    setPlaylistInfo(null);
    setTracks([]);
    setAudioFeatures([]);
    setError(null);
    setAnalysisProgress('');
    setAnalysisStep('fetching');
    setIsAnalyzing(false);
    setPlatform(null);
    setLastAttempt(null);
    localStorage.removeItem('lastPlaylistId');
  };

  return (
    <AppContext.Provider value={{
      currentPlaylistId, playlistInfo, tracks, audioFeatures,
      isAnalyzing, analysisProgress, analysisStep, error, platform,
      analyzePlaylist, retryAnalysis, clearPlaylist,
    }}>
      {children}
    </AppContext.Provider>
  );
};
